import {
  Conversation,
  FreeformMessage,
  ConversationParticipantType,
  PersonAvatar,
  Tag,
  MessageExpandableTagList,
  FileOptionsMenu,
  ViewFileMenuItemOption,
  DownloadFileMenuItemOption,
  MessageExpandableTagListProps,
  User,
  MessagingExpandableTagListItem,
} from '@elements-financial/elements';
import Linkify from 'linkify-react';
import { isFreeFormMessage, sentAt } from '../../Message/util/message.util';
import clsx from 'clsx';
import { IntermediateRepresentation } from 'linkifyjs';

interface OptimisticSelfParticipantData {
  userID: string;
  profileImage?: string | null;
  profileColor?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  type: ConversationParticipantType;
}

export interface ConversationThreadMessageProps {
  className?: string;
  message: FreeformMessage;
  conversation?: Conversation;
  self: User | undefined | null;
  isScrollingMessageThread?: boolean;
  messageExpandableTagListProps: Pick<
    MessageExpandableTagListProps,
    'expandedTagListID' | 'setExpandedTagListID' | 'floatingOverrides'
  >;
  onboardingReminderElement?: React.ReactNode;
  optimisticSelfParticipantType: ConversationParticipantType;
  linkifyAnchorRenderer?: (props: IntermediateRepresentation) => JSX.Element | undefined | null | boolean;
}

/**
 *
 * @param isScrollingMessageThread - This prop is used to disable the animation of the file list when the user is scrolling the message thread.
 * this is due to a virtualized message thread that causes the animation to be janky when scrolling.
 */
export function ConversationThreadMessage({
  self,
  message,
  className,
  conversation,
  isScrollingMessageThread,
  onboardingReminderElement,
  optimisticSelfParticipantType,
  messageExpandableTagListProps,
  linkifyAnchorRenderer,
}: ConversationThreadMessageProps) {
  const optimisticSelfParticipantData: OptimisticSelfParticipantData | undefined = self
    ? {
        userID: self?.id,
        givenName: self?.givenName,
        familyName: self?.familyName,
        profileColor: self?.profileColor,
        profileImage: self?.profileImage,
        type: optimisticSelfParticipantType,
      }
    : undefined;
  const participant = conversation?.participants.find((p) => p.id === message.sender) ?? optimisticSelfParticipantData;
  const isSelf = participant?.userID === self?.id;
  const isFirmMember = participant?.type === ConversationParticipantType.FirmMember;
  const showResponseOptions =
    isFirmMember && isFreeFormMessage(message) && conversation?.latestMessageID === message.id;
  const tagItems: MessagingExpandableTagListItem[] | undefined = message.attachments?.map((file) => ({
    file,
    type: 'file',
  }));

  if (!participant) return null;

  return (
    <div className={clsx(className, 'ConversationThreadMessage')}>
      <div className="flex items-start">
        <PersonAvatar person={participant} className="mr-3" />
        <div className="flex-1 text-sm leading-[16.8px]">
          <div className="text-light-gray flex justify-between text-right opacity-70">
            <p className="text-left text-darkMode-gray-light">
              {`${!isSelf ? `${participant.givenName} ${participant.familyName}, ` : ''}${sentAt(message.createdAt)}`}
            </p>
          </div>
          <Linkify
            options={{
              target: '_blank',
              className: 'underline !text-palette-600',
              render: {
                url: (props) => linkifyAnchorRenderer?.(props) || <a {...props.attributes}>{props.content}</a>,
              },
            }}
          >
            <p className="leading[21.6] whitespace-pre-line text-lg  text-white">
              {isFreeFormMessage(message) && message.message}
            </p>
          </Linkify>
          {message.hasOnboardingReminder && onboardingReminderElement}
          {!!tagItems?.length && (
            <div className="empty:hidden mt-2 relative">
              <MessageExpandableTagList
                {...messageExpandableTagListProps}
                tagItems={tagItems}
                verticalPlacement="top"
                disableAnimation={isScrollingMessageThread}
                layoutIDPrefix={`conversation-thread-message-${message.id}`}
                tagItemRenderer={({ file }) =>
                  file && (
                    <FileOptionsMenu
                      inPortal
                      key={file.id}
                      offset={[0, 8]}
                      placement="top-start"
                      menuItems={[
                        <ViewFileMenuItemOption
                          fileName={file.name}
                          key={`view-file-${file.id}`}
                          downloadURL={file.downloadURL || ''}
                        />,
                        <DownloadFileMenuItemOption
                          fileName={file.name}
                          key={`download-file-${file.id}`}
                          downloadURL={file.downloadURL || ''}
                        />,
                      ]}
                    >
                      <Tag.Item
                        key={file.id}
                        label={file.name}
                        icon="document"
                        className="!w-auto !pointer-events-auto cursor-pointer hover:!bg-palette-600/40"
                        motionProps={{ layoutId: file.id, layout: isScrollingMessageThread ? 'position' : undefined }}
                      />
                    </FileOptionsMenu>
                  )
                }
              />
            </div>
          )}
        </div>
      </div>

      {showResponseOptions && (
        <div className="ml-[53px] flex-row flex-wrap flex gap-2">
          {message.suggestedResponses?.map((response, i) => (
            <span
              className="leading[14.4px] mr-1 rounded-xl bg-darkMode-gray-medium bg-opacity-40 px-2 py-1 text-xs text-darkMode-gray-light"
              key={`response-${i}`}
            >
              {response}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
